import React, { useEffect, useState } from "react";
import { Routes, Route } from "react-router-dom";
import { LayoutDashboard, Users, FileText, Wallet, Bell, UserPlus } from "lucide-react";
import { toast } from "sonner";
import client from "@/lib/api";
import PortalShell from "./PortalShell";
import { StatusBadge } from "@/components/Badges";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { NationalitySelect, OptionSelect, PhoneInput, GENDERS, isValidPhone, todayISO } from "@/components/FormControls";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";

const NAV = [
  { to: "/app/agency", end: true, label: "Dashboard", icon: LayoutDashboard },
  { to: "/app/agency/candidates", label: "Candidates", icon: Users },
  { to: "/app/agency/applications", label: "Applications", icon: FileText },
  { to: "/app/agency/commissions", label: "Commission", icon: Wallet },
  { to: "/app/agency/notifications", label: "Alerts", icon: Bell },
];

const EMPTY = { full_name: "", phone: { code: "", number: "" }, email: "", passport_number: "", nationality: "", gender: "", date_of_birth: "", position: "" };

function AddCandidate({ onDone }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(EMPTY);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const setV = (k) => (v) => setF({ ...f, [k]: v });
  const save = async (force = false) => {
    if (!f.full_name.trim()) return toast.error("Full name is required");
    if (!isValidPhone(f.phone.number)) return toast.error("Enter a valid phone number (6 to 14 digits)");
    const { phone, ...rest } = f;
    try {
      await client.post("/me/agency/candidates", { ...rest, mobile: phone.number, mobile_code: phone.code, force });
      toast.success("Candidate registered");
      setOpen(false); setF(EMPTY);
      onDone();
    } catch (e) {
      const d = e.response?.data?.detail || "Error";
      if (String(d).startsWith("Possible duplicate")) {
        if (window.confirm(`${d}. Register anyway?`)) return save(true);
      } else toast.error(d);
    }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="agency-add-candidate" className="bg-[#0088FF] hover:bg-[#0070E0]"><UserPlus className="h-4 w-4 mr-1" /> Add Candidate</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Register Candidate</DialogTitle></DialogHeader>
        <div className="grid sm:grid-cols-2 gap-3">
          <div><Label>Full Name *</Label><Input data-testid="agc-full_name" value={f.full_name} onChange={set("full_name")} /></div>
          <div><Label>Email</Label><Input data-testid="agc-email" type="email" value={f.email} onChange={set("email")} /></div>
          <div className="sm:col-span-2"><Label>Mobile</Label><PhoneInput testId="agc-mobile" value={f.phone} onChange={setV("phone")} /></div>
          <div><Label>Passport No.</Label><Input data-testid="agc-passport_number" value={f.passport_number} onChange={(e) => setF({ ...f, passport_number: e.target.value.toUpperCase() })} /></div>
          <div><Label>Nationality</Label><NationalitySelect testId="agc-nationality" value={f.nationality} onChange={setV("nationality")} /></div>
          <div><Label>Gender</Label><OptionSelect testId="agc-gender" value={f.gender} onChange={setV("gender")} options={GENDERS} /></div>
          <div><Label>Date of Birth</Label><Input data-testid="agc-date_of_birth" type="date" max={todayISO()} value={f.date_of_birth} onChange={set("date_of_birth")} /></div>
          <div className="sm:col-span-2"><Label>Position</Label><Input data-testid="agc-position" value={f.position} onChange={set("position")} /></div>
        </div>
        <DialogFooter><Button data-testid="agc-save" onClick={() => save(false)} className="bg-[#0088FF] hover:bg-[#0070E0]">Register</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Dashboard() {
  const [data, setData] = useState(null);
  const load = () => client.get("/me/agency/dashboard").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!data) return <div className="text-slate-400">Loading...</div>;
  const k = data.kpis;
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display text-xl font-bold text-[#0A192F]">{data.agency?.name}</h2>
          <div className="mt-1"><StatusBadge value={data.agency?.status} /></div>
        </div>
        <AddCandidate onDone={load} />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { l: "Total Candidates", v: k.total_candidates }, { l: "New", v: k.new_candidates },
          { l: "Pending Docs", v: k.pending_documents }, { l: "Interview Pending", v: k.interview_pending },
          { l: "Medical Pending", v: k.medical_pending }, { l: "Deployed", v: k.deployed },
          { l: "Commission Pending", v: k.commission_pending },
        ].map((x) => (
          <div key={x.l} className="bg-white rounded-2xl border border-slate-200 p-5">
            <div className="font-display text-3xl font-bold text-[#0A192F]">{x.v}</div>
            <div className="text-xs text-slate-500 mt-1">{x.l}</div>
          </div>
        ))}
      </div>
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100 font-display font-semibold text-[#0A192F]">Recent Candidates</div>
        <Table rows={data.candidates} />
      </div>
    </div>
  );
}

function Table({ rows }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
          <tr><th className="text-left px-5 py-2.5">Code</th><th className="text-left px-5 py-2.5">Name</th><th className="text-left px-5 py-2.5">Position</th><th className="text-left px-5 py-2.5">Stage</th><th className="text-left px-5 py-2.5">Status</th></tr>
        </thead>
        <tbody>
          {rows.map((c) => (
            <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50">
              <td className="px-5 py-3 text-slate-400">{c.code}</td>
              <td className="px-5 py-3 font-medium text-[#0A192F]">{c.full_name}</td>
              <td className="px-5 py-3">{c.position}</td>
              <td className="px-5 py-3"><StatusBadge value={c.current_stage} kind="stage" /></td>
              <td className="px-5 py-3"><StatusBadge value={c.current_status} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Candidates() {
  const [rows, setRows] = useState([]);
  const load = () => client.get("/me/agency/candidates").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  return (
    <div className="space-y-4">
      <div className="flex justify-end"><AddCandidate onDone={load} /></div>
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden"><Table rows={rows} /></div>
    </div>
  );
}

function Applications() {
  const [rows, setRows] = useState([]);
  useEffect(() => { client.get("/me/agency/applications").then((r) => setRows(r.data)).catch(() => {}); }, []);
  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-slate-500 text-xs uppercase"><tr><th className="text-left px-5 py-2.5">Candidate</th><th className="text-left px-5 py-2.5">Job</th><th className="text-left px-5 py-2.5">Country</th><th className="text-left px-5 py-2.5">Stage</th></tr></thead>
        <tbody>{rows.map((a) => (
          <tr key={a.id} className="border-t border-slate-100"><td className="px-5 py-3 font-medium">{a.candidate_name}</td><td className="px-5 py-3">{a.job_title}</td><td className="px-5 py-3">{a.country}</td><td className="px-5 py-3"><StatusBadge value={a.stage} kind="stage" /></td></tr>
        ))}</tbody>
      </table>
    </div>
  );
}

function Commissions() {
  const [rows, setRows] = useState([]);
  useEffect(() => { client.get("/me/agency/commissions").then((r) => setRows(r.data)).catch(() => {}); }, []);
  const total = rows.reduce((s, r) => s + (r.amount || 0), 0);
  const pending = rows.filter((r) => r.status === "Pending").reduce((s, r) => s + (r.amount || 0), 0);
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4 max-w-md">
        <div className="bg-white rounded-2xl border border-slate-200 p-5"><div className="font-display text-2xl font-bold text-[#0A192F]">₹{total.toLocaleString()}</div><div className="text-xs text-slate-500">Total Commission</div></div>
        <div className="bg-white rounded-2xl border border-slate-200 p-5"><div className="font-display text-2xl font-bold text-amber-600">₹{pending.toLocaleString()}</div><div className="text-xs text-slate-500">Pending</div></div>
      </div>
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase"><tr><th className="text-left px-5 py-2.5">Candidate</th><th className="text-left px-5 py-2.5">Client</th><th className="text-left px-5 py-2.5">Amount</th><th className="text-left px-5 py-2.5">Status</th></tr></thead>
          <tbody>{rows.map((c) => (
            <tr key={c.id} className="border-t border-slate-100"><td className="px-5 py-3">{c.candidate_name}</td><td className="px-5 py-3">{c.client_name}</td><td className="px-5 py-3 font-medium">₹{(c.amount || 0).toLocaleString()}</td><td className="px-5 py-3"><StatusBadge value={c.status} /></td></tr>
          ))}</tbody>
        </table>
      </div>
    </div>
  );
}

function Notifications() {
  const [list, setList] = useState([]);
  useEffect(() => { client.get("/me/notifications").then((r) => setList(r.data)).catch(() => {}); }, []);
  return (
    <div className="max-w-2xl space-y-2">
      {list.length === 0 && <div className="text-slate-400">No notifications.</div>}
      {list.map((n) => <div key={n.id} className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-3"><Bell className="h-4 w-4 text-[#0088FF]" /><div className="text-sm text-slate-700">{n.title}</div></div>)}
    </div>
  );
}

export default function AgencyPortal() {
  return (
    <PortalShell nav={NAV} roleLabel="Agency" title="Agency Portal">
      <Routes>
        <Route index element={<Dashboard />} />
        <Route path="candidates" element={<Candidates />} />
        <Route path="applications" element={<Applications />} />
        <Route path="commissions" element={<Commissions />} />
        <Route path="notifications" element={<Notifications />} />
      </Routes>
    </PortalShell>
  );
}
